<template>
  <div class="view-container">
    <div class="stats-row">
      <div class="stat-card" v-for="s in stats" :key="s.label" :style="{ borderTopColor: s.color }">
        <div class="stat-icon" :style="{ background: s.color }"><i :class="s.icon"></i></div>
        <div class="stat-info">
          <div class="stat-value">{{ s.value }}</div>
          <div class="stat-label">{{ s.label }}</div>
        </div>
      </div>
    </div>

    <div class="content-grid">
      <div class="grid-left">
        <div class="card">
          <div class="card-header"><h3><i class="fas fa-shield-alt"></i> 授权使用情况</h3></div>
          <div class="card-body">
            <div class="license-bar">
              <div class="license-info"><span>已授权</span><span class="license-count">{{ authorizedCount }} / {{ maxUsers }}</span></div>
              <div class="license-track">
                <div class="license-fill" :style="{ width: (authorizedCount / maxUsers * 100) + '%' }"></div>
              </div>
              <div class="license-status" :class="{ full: authorizedCount >= maxUsers }">
                {{ authorizedCount >= maxUsers ? '授权已满' : '剩余 ' + (maxUsers - authorizedCount) + ' 个名额' }}
              </div>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="card-header"><h3><i class="fas fa-history"></i> 最近审批记录</h3></div>
          <div class="card-body no-padding">
            <div v-for="log in recentApprovals" :key="log.id" class="log-item">
              <div class="log-avatar" :style="{ background: log.color }">{{ log.avatar }}</div>
              <div class="log-info">
                <div class="log-top"><span class="log-user">{{ log.userName }}</span><span class="log-time">{{ log.time }}</span></div>
                <div class="log-detail">{{ log.action }} · {{ log.device }}</div>
              </div>
              <span :class="['log-status', log.result]">{{ log.resultText }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="grid-right">
        <div class="card">
          <div class="card-header"><h3><i class="fas fa-chart-line"></i> 月度审批趋势</h3></div>
          <div class="card-body"><VChart class="chart" :option="trendChart" autoresize /></div>
        </div>
        <div class="card">
          <div class="card-header"><h3><i class="fas fa-mobile-alt"></i> 设备分布</h3></div>
          <div class="card-body">
            <VChart class="chart" :option="deviceChart" autoresize />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { PieChart, BarChart, LineChart } from 'echarts/charts'
import { TitleComponent, TooltipComponent, LegendComponent, GridComponent } from 'echarts/components'
import VChart from 'vue-echarts'

use([CanvasRenderer, PieChart, BarChart, LineChart, TitleComponent, TooltipComponent, LegendComponent, GridComponent])

const maxUsers = ref(200)
const authorizedCount = ref(156)

const stats = ref([
  { label: '已授权用户', value: authorizedCount.value, icon: 'fas fa-users', color: '#8b5cf6' },
  { label: '在线用户', value: 42, icon: 'fas fa-wifi', color: '#10b981' },
  { label: '绑定设备', value: 189, icon: 'fas fa-mobile-alt', color: '#f59e0b' },
  { label: '今日审批', value: 86, icon: 'fas fa-check-circle', color: '#3b82f6' }
])

const recentApprovals = ref([
  { id: 1, userName: '张三', avatar: '张', color: '#3b82f6', action: '通过了采购申请', device: 'iPhone 15 Pro', time: '刚刚', result: 'approved', resultText: '通过' },
  { id: 2, userName: '李四', avatar: '李', color: '#10b981', action: '驳回了费用报销', device: '华为 Mate 60', time: '10:32', result: 'rejected', resultText: '驳回' },
  { id: 3, userName: '王五', avatar: '王', color: '#f59e0b', action: '审批了请假申请', device: 'Xiaomi 14', time: '09:15', result: 'approved', resultText: '通过' },
  { id: 4, userName: '赵六', avatar: '赵', color: '#8b5cf6', action: '通过了合同审批', device: 'OPPO Find X7', time: '08:50', result: 'approved', resultText: '通过' },
  { id: 5, userName: '孙七', avatar: '孙', color: '#ef4444', action: '转交了出差申请', device: 'iPhone 14', time: '昨天', result: 'transferred', resultText: '转交' }
])

const trendChart = ref({
  tooltip: { trigger: 'axis' },
  grid: { left: 45, right: 20, top: 20, bottom: 30 },
  xAxis: { type: 'category', data: ['1月','2月','3月','4月','5月','6月'], axisLabel: { fontSize: 10 } },
  yAxis: { type: 'value', axisLabel: { fontSize: 10 } },
  series: [
    { name: '审批通过', type: 'bar', data: [320, 380, 420, 450, 510, 480], itemStyle: { color: '#10b981', borderRadius: [3,3,0,0] } },
    { name: '审批驳回', type: 'bar', data: [25, 18, 32, 22, 28, 20], itemStyle: { color: '#ef4444', borderRadius: [3,3,0,0] } },
    { name: '总审批量', type: 'line', data: [345, 398, 452, 472, 538, 500], smooth: true, itemStyle: { color: '#8b5cf6' } }
  ]
})

const deviceChart = ref({
  tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
  series: [{
    type: 'pie', radius: ['50%','75%'], center: ['50%','50%'],
    data: [
      { value: 68, name: 'iPhone', itemStyle: { color: '#8b5cf6' } },
      { value: 55, name: '华为', itemStyle: { color: '#ef4444' } },
      { value: 32, name: 'Xiaomi', itemStyle: { color: '#f59e0b' } },
      { value: 18, name: 'OPPO', itemStyle: { color: '#10b981' } },
      { value: 16, name: '其他', itemStyle: { color: '#6b7280' } }
    ],
    label: { fontSize: 10, formatter: '{b}\n{d}%' }
  }]
})
</script>

<style scoped>
.view-container { padding: 20px; background: #f5f7fa; overflow-y: auto; max-height: calc(100vh - 130px); }
.stats-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 20px; }
.stat-card { background: #fff; border-radius: 8px; border: 1px solid #e8ecf1; border-top: 3px solid #8b5cf6; padding: 20px; display: flex; align-items: center; gap: 16px; }
.stat-icon { width: 48px; height: 48px; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 20px; }
.stat-value { font-size: 28px; font-weight: 700; color: #1f2937; }
.stat-label { font-size: 13px; color: #6b7280; margin-top: 2px; }

.content-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.grid-left, .grid-right { display: flex; flex-direction: column; gap: 20px; }
.card { background: #fff; border-radius: 8px; border: 1px solid #e8ecf1; overflow: hidden; }
.card-header { padding: 12px 16px; border-bottom: 1px solid #e8ecf1; background: #fafbfc; }
.card-header h3 { margin: 0; font-size: 14px; font-weight: 600; color: #374151; display: flex; align-items: center; gap: 8px; }
.card-body { padding: 16px; }
.card-body.no-padding { padding: 0; }

.license-bar { }
.license-info { display: flex; justify-content: space-between; font-size: 13px; color: #374151; margin-bottom: 8px; }
.license-count { font-weight: 700; color: #8b5cf6; }
.license-track { height: 10px; background: #f3f4f6; border-radius: 5px; margin-bottom: 8px; overflow: hidden; }
.license-fill { height: 100%; background: linear-gradient(90deg, #8b5cf6, #6d28d9); border-radius: 5px; transition: width 0.5s ease; }
.license-status { font-size: 12px; color: #10b981; font-weight: 500; }
.license-status.full { color: #ef4444; }

.log-item { display: flex; align-items: center; gap: 10px; padding: 10px 16px; border-bottom: 1px solid #f3f4f6; }
.log-avatar { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 12px; font-weight: 600; flex-shrink: 0; }
.log-info { flex: 1; min-width: 0; }
.log-top { display: flex; justify-content: space-between; margin-bottom: 2px; }
.log-user { font-size: 12px; font-weight: 600; color: #374151; }
.log-time { font-size: 10px; color: #9ca3af; }
.log-detail { font-size: 11px; color: #6b7280; }
.log-status { font-size: 10px; padding: 2px 8px; border-radius: 3px; font-weight: 500; flex-shrink: 0; }
.log-status.approved { background: #dcfce7; color: #16a34a; }
.log-status.rejected { background: #fee2e2; color: #dc2626; }
.log-status.transferred { background: #fef3c7; color: #d97706; }

.chart { height: 260px; }
</style>
